import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AppHeader, AppShell, cn } from '@beatq/ui';
import {
  AccountMenu,
  parseAccessTokenClaims,
  SubjectImpersonationBanner,
  useAuth,
  usePermissions,
} from '@beatq/shared';
import {
  BUSINESS_ROOT_NAV,
  activeBusinessNavItem,
  filterBusinessNav,
  isBusinessNavChildCurrent,
  isBusinessNavItemCurrent,
} from './businessNav.js';
import { BizNavIcon } from './businessIcons.js';
import { ensureBusinessAccountContext } from './ensureBusinessContext.js';
import { setStoredBusinessAccountId } from './businessContext.js';
import { businessRuntimeLabel } from './businessChrome.js';
import { businessOpsPollMs } from './businessRealtime.js';
import { BusinessCrumbsProvider, useBusinessCrumbsOverride } from './businessCrumbsContext.js';
import { buildBusinessCrumbs, shouldShowBusinessCrumbs } from './businessCrumbs.js';
import { landingPathForAccount } from './businessLanding.js';
import { businessRoleLabel } from './businessRole.js';
import {
  accountFromEnvelope,
  getBusinessAccount,
  loadSetupChecklist,
} from '../features/signup-setup/signupSetupApi.js';
import { businessAccountContextName, initialsFromLabel } from '../features/_ui/businessUi.js';
import {
  alertsFromEnvelope,
  loadAlertsPanel,
} from '../features/invoices-reports/invoicesReportsApi.js';
import {
  approvalsFromEnvelope,
  listApprovals,
} from '../features/policies-approvals/policiesApprovalsApi.js';
import { bellUnreadCount, navBadgeMap, pendingApprovalBadgeCount } from './navBadges.js';
import { useBusinessApprovalsRealtime } from '../features/policies-approvals/useBusinessApprovalsRealtime.js';

function navClassName(isActive: boolean, extra?: string) {
  return cn('biz-nav-link', isActive && 'is-active', extra);
}

const BELL_UNREAD_POLL_MS = 15_000;

function BusinessShellChrome() {
  const { authenticated, signInPath, accessToken } = useAuth();
  const { hasPerm } = usePermissions();
  const location = useLocation();
  const crumbsOverride = useBusinessCrumbsOverride();
  const [contextReady, setContextReady] = useState(false);
  const [contextError, setContextError] = useState<string | null>(null);
  const canReadApprovals =
    authenticated &&
    (hasPerm('PERM-approval.request.read') || hasPerm('PERM-approval.request.decide'));
  const canReadAlerts = authenticated && hasPerm('PERM-notification.history.read');
  const { live: socketsLive } = useBusinessApprovalsRealtime(
    authenticated && contextReady && canReadApprovals,
  );
  const approvalPollMs = businessOpsPollMs(socketsLive, 15_000);
  const runtimeText = businessRuntimeLabel({
    production: import.meta.env.PROD === true,
    socketsLive,
  });

  const accountQuery = useQuery({
    queryKey: ['business', 'account', 'shell-identity'],
    queryFn: () => getBusinessAccount(),
    enabled: authenticated && contextReady,
    staleTime: 60_000,
  });
  const checklistQuery = useQuery({
    queryKey: ['business', 'setup-checklist', 'shell-landing'],
    queryFn: () => loadSetupChecklist(),
    enabled: authenticated && contextReady,
    staleTime: 60_000,
  });
  const account = accountFromEnvelope(accountQuery.data) ?? checklistQuery.data?.account;
  const accountLabel = businessAccountContextName({
    legalName: account?.legalName,
    tradingName: account?.tradingName,
  });
  const initials = initialsFromLabel(accountLabel);
  const roleLabel = businessRoleLabel(parseAccessTokenClaims(accessToken).rol);
  const homeHref = landingPathForAccount({
    status: account?.status,
    state: account?.state,
    onboardedAt: account?.onboardedAt,
    setupProgress: account?.setupProgress,
    checklistProgress: checklistQuery.data?.progress,
  });
  const hasAccountPendingCount =
    typeof account?.pendingApprovalsCount === 'number' &&
    Number.isFinite(account.pendingApprovalsCount);

  const approvalsQuery = useQuery({
    queryKey: ['business', 'approvals', 'nav-badge'],
    queryFn: () => listApprovals(),
    enabled: authenticated && contextReady && canReadApprovals && !hasAccountPendingCount,
    staleTime: 15_000,
    refetchInterval: approvalPollMs,
  });
  const alertsQuery = useQuery({
    queryKey: ['business', 'alerts', 'nav-badge'],
    queryFn: () => loadAlertsPanel({ unreadOnly: true }),
    enabled: authenticated && contextReady && canReadAlerts,
    staleTime: 15_000,
    refetchInterval: BELL_UNREAD_POLL_MS,
  });

  const approvalCount = pendingApprovalBadgeCount(
    account?.pendingApprovalsCount,
    approvalsFromEnvelope(approvalsQuery.data),
  );
  const alertCount = bellUnreadCount(alertsFromEnvelope(alertsQuery.data));

  const badgeByHref = useMemo(() => navBadgeMap(Number(approvalCount) || 0), [approvalCount]);

  useEffect(() => {
    let cancelled = false;
    void ensureBusinessAccountContext()
      .then(() => {
        if (!cancelled) {
          setContextReady(true);
          setContextError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setContextError(err instanceof Error ? err.message : 'Failed to set business context');
          setContextReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(
    () => filterBusinessNav(BUSINESS_ROOT_NAV, hasPerm, authenticated),
    [hasPerm, authenticated],
  );
  const section = activeBusinessNavItem(visible, location.pathname);
  const sectionChildren = section?.children ?? [];
  const crumbs = buildBusinessCrumbs(location.pathname, {
    lastLabel: crumbsOverride.crumbLabel,
    breadcrumbs: crumbsOverride.breadcrumbs,
  });
  const showCrumbs = shouldShowBusinessCrumbs(crumbs);
  const showBell = canReadAlerts;

  return (
    <div className="business-portal w-full max-w-none">
      <AppShell
        className="w-full max-w-none"
        banners={<SubjectImpersonationBanner />}
        header={
          <AppHeader
            productName="BeatQ Eats Business"
            homeHref={homeHref}
            renderBrand={({ homeHref: href }) => (
              <Link to={href} className="biz-wordmark">
                <span className="biz-wordmark-beatq">BeatQ Eats</span>
                <span className="biz-wordmark-business">Business</span>
              </Link>
            )}
            actions={
              <div className="flex items-center gap-inline">
                {showBell ? (
                  <Link
                    to="/alerts-panel"
                    className="b01-bell"
                    aria-label={alertCount > 0 ? `Alerts, ${alertCount} unread` : 'Alerts'}
                    data-testid="business-alerts-bell"
                  >
                    <BizNavIcon icon="alerts" />
                    {alertCount > 0 ? (
                      <span className="b01-bell-count" aria-hidden>
                        {alertCount > 99 ? '99+' : alertCount}
                      </span>
                    ) : null}
                  </Link>
                ) : null}
                <p
                  className="biz-runtime-status"
                  data-testid="business-runtime-status"
                  data-sockets={socketsLive ? 'live' : 'poll'}
                  title={runtimeText}
                >
                  {runtimeText}
                </p>
                <AccountMenu
                  label={accountLabel}
                  subtitle={roleLabel ?? 'Business'}
                  onAfterLogout={() => setStoredBusinessAccountId(null)}
                  signInPath={signInPath || '/corporate-sign-in'}
                />
              </div>
            }
          />
        }
        nav={
          <nav
            aria-label="Business portal primary"
            className={cn(
              'biz-sidebar flex shrink-0 flex-col p-inline',
              'md:sticky md:top-0 md:w-60',
              'md:h-[calc(100vh-3.5rem)] md:max-h-[calc(100vh-3.5rem)] md:overflow-hidden',
            )}
            data-testid="business-sidebar"
          >
            <div className="m-0 flex min-h-0 flex-col gap-xs overflow-y-auto p-0 flex-1">
              <ul className="m-0 flex list-none flex-col gap-xs p-0">
                {visible.map((item) => {
                  const current = isBusinessNavItemCurrent(item, location.pathname);
                  const count = badgeByHref[item.href];
                  const nested = current ? (item.children ?? []) : [];
                  return (
                    <li key={item.label}>
                      <NavLink
                        to={item.href}
                        className={() =>
                          navClassName(current, item.promoted ? 'b01-nav-promoted' : undefined)
                        }
                        end={item.href === '/account-overview'}
                        data-nav-screen={item.screenId}
                        data-nav-promoted={item.promoted ? 'true' : undefined}
                      >
                        <BizNavIcon icon={item.icon} />
                        <span className="biz-nav-label">{item.label}</span>
                        {count != null && count > 0 ? (
                          <span className="biz-nav-badge" aria-label={`${count} pending`}>
                            {count > 99 ? '99+' : count}
                          </span>
                        ) : null}
                      </NavLink>
                      {nested.length > 0 ? (
                        <ul className="b01-nav-nested m-0 mt-xs flex list-none flex-col gap-xs p-0">
                          {nested.map((child) => (
                            <li key={child.href}>
                              <NavLink
                                to={child.href}
                                className={() =>
                                  navClassName(
                                    isBusinessNavChildCurrent(child, location.pathname),
                                    'b01-nav-child',
                                  )
                                }
                                data-nav-screen={child.screenId}
                              >
                                <span className="biz-nav-label">{child.label}</span>
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className="biz-sidebar-footer">
              <div className="biz-identity-chip">
                <span className="biz-avatar" aria-hidden>
                  {initials}
                </span>
                <span className="min-w-0">
                  <span className="biz-identity-name block truncate">{accountLabel}</span>
                  {roleLabel ? (
                    <span className="biz-identity-role block truncate">{roleLabel}</span>
                  ) : null}
                </span>
              </div>
            </div>
          </nav>
        }
      >
        {contextError ? (
          <p role="alert" className="mb-field text-negative">
            {contextError}
          </p>
        ) : null}
        {!contextReady ? (
          <p role="status">Preparing account context…</p>
        ) : (
          <>
            {showCrumbs ? (
              <nav aria-label="Breadcrumb" className="b01-crumbs">
                <ol className="b01-crumbs-list">
                  {crumbs.map((crumb, i) => {
                    const last = i === crumbs.length - 1;
                    return (
                      <li key={`${crumb.label}-${i}`} className="b01-crumbs-item">
                        {last || !crumb.href ? (
                          <span aria-current={last ? 'page' : undefined}>{crumb.label}</span>
                        ) : (
                          <Link to={crumb.href}>{crumb.label}</Link>
                        )}
                        {last ? null : (
                          <span aria-hidden="true" className="b01-crumbs-sep">
                            ›
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </nav>
            ) : null}
            {sectionChildren.length > 0 ? (
              <nav
                className="b01-section-subnav"
                aria-label={`${section?.label ?? 'Section'} pages`}
                data-testid="business-section-subnav"
              >
                {sectionChildren.map((child) => (
                  <NavLink
                    key={child.href}
                    to={child.href}
                    className={() =>
                      cn(
                        'b01-section-subnav-link',
                        isBusinessNavChildCurrent(child, location.pathname) && 'is-active',
                      )
                    }
                    data-nav-screen={child.screenId}
                  >
                    {child.label}
                  </NavLink>
                ))}
              </nav>
            ) : null}
            <Outlet />
          </>
        )}
      </AppShell>
    </div>
  );
}

export function BusinessShell() {
  return (
    <BusinessCrumbsProvider>
      <BusinessShellChrome />
    </BusinessCrumbsProvider>
  );
}
